import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CHANNEL_PLATFORMS, PLATFORMS, type ChannelPlatform, type ChannelView } from "@joogoos/contracts";
import { api, here } from "../api.js";

/**
 * The platforms that keep one grant per account however many workspaces use it, so disconnecting one does not give it
 * back while another still uses the account (ADR 0059; `channelGrantStillUsed` in the db package holds the same list).
 */
const ONE_GRANT: ReadonlySet<ChannelPlatform> = new Set(["youtube", "facebook"]);

/**
 * **Business → Channels** ([25](../../../../docs/25-channels-and-publishing.md),
 * [ADR 0038](../../../../docs/adr/0038-a-channel-is-a-connection-a-post-is-a-row-and-the-worker-is-the-only-thing-that-publishes.md)).
 *
 * Where the accounts this business posts to are connected — beside the mailbox, and for the same reason it is
 * there rather than in Calendar: which Instagram this company posts as is a fact about the business, like its
 * address and its name, and it is not a thing you want to go looking for on the day you want to schedule a post.
 *
 * Six rows, always all six, whether they are connected or not. A page that showed only what was connected would
 * leave a customer with nothing to press and no idea the other five existed. Each row says one of three things:
 * connect it, this box cannot, or here is what is connected and what it needs.
 *
 * Nothing here can show a token. The API has no field to put one in.
 */

/** What the callback says when the browser comes back, in the words the page shows. */
const OUTCOMES: Record<string, { tone: "good" | "bad"; says: string }> = {
  cancelled: { tone: "bad", says: "Nothing was connected — the sign-in was cancelled." },
  expired: { tone: "bad", says: "That took too long, or was started somewhere else. Try again." },
  not_allowed: { tone: "bad", says: "Only an owner of this workspace can connect an account." },
  failed: { tone: "bad", says: "That platform did not finish the connection. Nothing was changed." },
  unavailable: { tone: "bad", says: "This box cannot connect that account." },
};

/** The one query key, so connecting, pausing and disconnecting all redraw the same page. */
const KEY = ["channels"];

export function ChannelsPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api.channels() });
  /**
   * The word the callback redirected back with (`/?channel=connected:instagram`).
   *
   * Read once and taken out of the URL, so a reload does not say "connected" again about something that happened
   * ten minutes ago — the same rule the mailbox, the exports and the report links follow.
   */
  const [outcome, setOutcome] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const said = params.get("channel");
    if (!said) return;
    setOutcome(said);
    params.delete("channel");
    const rest = params.toString();
    history.replaceState(history.state, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
    void qc.invalidateQueries({ queryKey: KEY });
  }, [qc]);

  const connect = useMutation({
    mutationFn: (platform: ChannelPlatform) => api.connectChannel(platform),
    // The platform's own screen, not ours: this app hands the browser over and gets it back on the callback.
    onSuccess: (r) => { location.href = r.url; },
  });
  const pause = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => api.saveChannel(id, { paused }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const choose = useMutation({
    mutationFn: ({ id, choiceId }: { id: string; choiceId: string }) => api.saveChannel(id, { choiceId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const kids = useMutation({
    mutationFn: ({ id, madeForKids }: { id: string; madeForKids: boolean | null }) => api.saveChannel(id, { madeForKids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => api.disconnectChannel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  const d = q.data;
  const connected = new Map((d?.channels ?? []).map((c) => [c.platform, c]));
  // Which account arrived, by name, once the list has read again: an owner with two X accounts needs to see which
  // one X handed over, not only that one did.
  const arrived = outcome?.startsWith("connected:") ? connected.get(outcome.slice(10) as ChannelPlatform) : undefined;
  const said = outcome?.startsWith("connected:")
    ? {
      tone: "good" as const,
      says: `${PLATFORMS[outcome.slice(10) as ChannelPlatform]?.label ?? "That account"} is connected${arrived ? ` as ${arrived.handle ? `@${arrived.handle}` : arrived.accountName}` : ""}. Posts can go out on it.`,
    }
    : outcome && Object.hasOwn(OUTCOMES, outcome) ? OUTCOMES[outcome] : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="text-lg font-semibold">Channels</h1>
        <span className="text-[12.5px] text-muted">where your posts go</span>
      </div>

      {said ? (
        <p className={`text-[13px] rounded-lg px-3 py-2 ${said.tone === "good" ? "bg-tint text-ink" : "border border-line-2 bg-panel text-ink-2"}`}>
          {said.says}
        </p>
      ) : null}

      <p className="text-[13px] leading-relaxed text-ink-2">
        Connect an account once and Joogoos can put a finished post on it at a time you choose. Each platform asks you
        to sign in on its own site and to allow posting — nothing else. You can take any of them away again here, and
        doing so gives the permission back to the platform.
      </p>

      {q.isPending ? <p className="text-sm text-faint">…</p> : null}
      {d && !d.available ? <p className="text-[13px] text-muted">{d.note ?? "Not available yet."}</p> : null}

      <div className="flex flex-col gap-2">
        {CHANNEL_PLATFORMS.map((platform) => (
          <Row
            key={platform}
            platform={platform}
            channel={connected.get(platform) ?? null}
            canConnect={Boolean(d?.connectable.includes(platform))}
            mayConnect={Boolean(d?.mayConnect)}
            connecting={connect.isPending && connect.variables === platform}
            onConnect={() => connect.mutate(platform)}
            onPause={(paused) => { const c = connected.get(platform); if (c) pause.mutate({ id: c.id, paused }); }}
            onDisconnect={() => { const c = connected.get(platform); if (c) disconnect.mutate(c.id); }}
            onChoose={(choiceId) => { const c = connected.get(platform); if (c) choose.mutate({ id: c.id, choiceId }); }}
            onKids={(madeForKids) => { const c = connected.get(platform); if (c) kids.mutate({ id: c.id, madeForKids }); }}
            busy={pause.isPending || disconnect.isPending}
          />
        ))}
      </div>

      {d && !d.mayConnect && d.available
        ? <p className="text-xs text-muted">Only an owner of this workspace can connect or take away an account.</p>
        : null}
      {connect.error ? <p className="text-xs text-bad">{String(connect.error)}</p> : null}
    </div>
  );
}

/**
 * One platform, in whichever of its three states it is in.
 *
 * All six are always drawn, including the ones this box has no keys for. Saying "not on this box yet" is a fact a
 * customer can act on — ask, or wait — where a missing row is just a product that seems not to do the thing.
 */
function Row({ platform, channel, canConnect, mayConnect, connecting, onConnect, onPause, onDisconnect, onChoose, onKids, busy }: {
  platform: ChannelPlatform;
  channel: ChannelView | null;
  canConnect: boolean;
  mayConnect: boolean;
  connecting: boolean;
  onConnect: () => void;
  onPause: (paused: boolean) => void;
  onDisconnect: () => void;
  onChoose: (choiceId: string) => void;
  onKids: (madeForKids: boolean | null) => void;
  busy: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const traits = PLATFORMS[platform];
  const paused = channel?.status === "paused";
  const account = channel?.handle ? `@${channel.handle}` : channel?.accountName;

  return (
    <div className="rounded-xl border border-line bg-panel p-3.5 flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Logo platform={platform} />
        <span className="text-sm font-semibold">{traits.label}</span>
        {channel ? (
          <span className="flex items-center gap-1.5 text-xs text-muted">
            <span className={`w-2 h-2 rounded-full ${channel.status === "connected" ? "bg-good" : channel.status === "paused" ? "bg-line-2" : "bg-bad"}`} />
            {channel.avatarUrl ? <AccountPicture channelId={channel.id} /> : null}
            {/* The account itself, on the network, so which one is connected can be checked with a click. */}
            {channel.url ? (
              <a href={channel.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-ink">{account}</a>
            ) : account}
          </span>
        ) : null}
        {channel && channel.waiting > 0 ? (
          <span className="text-[11px] text-muted tabular-nums">{channel.waiting} waiting</span>
        ) : null}

        <span className="ml-auto flex items-center gap-2">
          {!channel && canConnect ? (
            <button
              onClick={onConnect}
              disabled={!mayConnect || connecting}
              className="px-3 py-1.5 rounded-lg bg-ink text-white text-xs font-semibold disabled:opacity-40"
            >
              Connect
            </button>
          ) : null}
          {!channel && !canConnect ? <span className="text-xs text-faint">not available yet</span> : null}
          {channel && mayConnect ? (
            <>
              <button
                onClick={() => onPause(!paused)}
                disabled={busy || channel.status === "disconnected"}
                className="px-2.5 py-1.5 rounded-lg border border-line-2 bg-panel text-xs font-medium disabled:opacity-40"
              >
                {paused ? "Resume" : "Pause"}
              </button>
              {channel.needsReauth ? (
                <button onClick={onConnect} disabled={connecting} className="px-2.5 py-1.5 rounded-lg bg-coral text-white text-xs font-semibold">
                  Connect again
                </button>
              ) : null}
            </>
          ) : null}
        </span>
      </div>

      {channel && channel.choices.length > 1 ? (
        // The sign-in can post as several — a Facebook account's Pages. The first was taken; this says which, and
        // changing it moves the posts still waiting too (32).
        <label className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-muted">Posts go to</span>
          <select
            value={channel.choiceId ?? ""}
            disabled={!mayConnect || busy}
            onChange={(e) => onChoose(e.target.value)}
            aria-label={`Which ${traits.label} account posts go to`}
            className="px-2 py-1 rounded-lg border border-line-2 bg-page text-[12.5px]"
          >
            {channel.choices.map((ch) => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
          </select>
          {channel.waiting > 0 ? <span className="text-[11.5px] text-faint">and the posts waiting move with it</span> : null}
        </label>
      ) : null}

      {channel && platform === "youtube" ? (
        // YouTube needs the answer on every upload, not the question every time (32): said once here, and the
        // publish dialog starts from it.
        <label className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="text-muted">Videos on this channel are</span>
          <select
            value={channel.madeForKids === null ? "ask" : channel.madeForKids ? "kids" : "not"}
            disabled={!mayConnect || busy}
            onChange={(e) => onKids(e.target.value === "ask" ? null : e.target.value === "kids")}
            aria-label="Whether this channel's videos are made for kids"
            className="px-2 py-1 rounded-lg border border-line-2 bg-page text-[12.5px]"
          >
            <option value="not">not made for kids</option>
            <option value="kids">made for kids</option>
            <option value="ask">— ask me for each video</option>
          </select>
        </label>
      ) : null}

      {channel?.needsReauth ? (
        <p className="text-[12.5px] rounded-lg border border-line-2 px-3 py-2">
          {traits.label} has stopped accepting this connection, so nothing can go out on it. Connect it again.
          {channel.lastError ? <span className="block text-xs text-muted mt-1">{channel.lastError}</span> : null}
        </p>
      ) : null}

      {channel && !channel.renewable && channel.expiresAt && !channel.needsReauth ? (
        // LinkedIn's permission lasts sixty days and is not renewed (32): say by when, a week ahead in the page's accent.
        <p className={`text-[12.5px] ${new Date(channel.expiresAt).getTime() - Date.now() < 7 * 86_400_000 ? "text-coral font-medium" : "text-muted"}`}>
          Connect it again by {new Date(channel.expiresAt).toLocaleDateString()} — {traits.label} does not renew the connection, and posts stop after that.
        </p>
      ) : null}

      {channel ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-faint">
          <span>connected {new Date(channel.connectedAt).toLocaleDateString()}</span>
          <span>
            {channel.lastPublishedAt ? `last post ${new Date(channel.lastPublishedAt).toLocaleDateString()}` : "nothing posted yet"}
          </span>
          {mayConnect ? (
            confirming ? (
              <span className="ml-auto flex items-center gap-2">
                <span className="text-muted">
                  This gives the permission back to {traits.label}{ONE_GRANT.has(channel.platform) ? " once no other workspace uses this account" : ""}.
                </span>
                <button onClick={() => { setConfirming(false); onDisconnect(); }} disabled={busy} className="px-2.5 py-1.5 rounded-lg bg-bad text-white font-semibold disabled:opacity-40">
                  Disconnect
                </button>
                <button onClick={() => setConfirming(false)} className="px-2.5 py-1.5 rounded-lg border border-line-2">Keep it</button>
              </span>
            ) : (
              <button onClick={() => setConfirming(true)} className="ml-auto text-bad font-medium">Disconnect</button>
            )
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          <p className="text-xs text-muted leading-relaxed">{traits.needs}.</p>
          {canConnect ? <p className="text-xs text-ink-2 leading-relaxed">{traits.before}.</p> : null}
          {traits.terms?.length ? (
            <p className="text-xs text-muted leading-relaxed">
              Connecting means agreeing to the{" "}
              {traits.terms.map((t, i) => (
                <span key={t.url}>
                  {i ? " and the " : ""}
                  <a href={t.url} target="_blank" rel="noreferrer" className="underline">{t.label}</a>
                </span>
              ))}.
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * A platform's mark, drawn rather than fetched.
 *
 * Simple shapes in the page's own ink, not the brands' logos: the CSP carries no remote images for this, a real
 * logo is somebody else's trademark with rules about how it may be shown, and six coloured badges would be the
 * loudest thing on a page that is otherwise quiet (the "warm studio" style).
 */
function Logo({ platform }: { platform: ChannelPlatform }) {
  const shape: Record<ChannelPlatform, string> = {
    instagram: "M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zM12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM17.5 6.5h.01",
    facebook: "M14 9V7a2 2 0 0 1 2-2h2V2h-3a5 5 0 0 0-5 5v2H7v3h3v10h4V12h3l1-3h-4z",
    tiktok: "M16 2a6 6 0 0 0 5 5v3a9 9 0 0 1-5-1.6V15a6 6 0 1 1-6-6v3a3 3 0 1 0 3 3V2h3z",
    youtube: "M22 8.5a3 3 0 0 0-2.1-2.1C18 6 12 6 12 6s-6 0-7.9.4A3 3 0 0 0 2 8.5 31 31 0 0 0 2 12a31 31 0 0 0 .1 3.5 3 3 0 0 0 2 2.1C6 18 12 18 12 18s6 0 7.9-.4a3 3 0 0 0 2.1-2.1A31 31 0 0 0 22 12a31 31 0 0 0 0-3.5zM10 15V9l5 3-5 3z",
    linkedin: "M4 9h4v12H4zM6 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM11 9h4v2a4 4 0 0 1 7 3v7h-4v-6a2 2 0 0 0-4 0v6h-3z",
    x: "M3 3l7.5 9.5L3.5 21H6l5.6-6.6L16.5 21H21l-7.8-10L20.5 3H18l-5.2 6.1L8.3 3z",
  };
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0 text-ink-2">
      <path d={shape[platform]} />
    </svg>
  );
}

/**
 * The account's own picture, beside its name, so which account is linked is seen and not only read (32). Served by the
 * API, which fetches it from the platform's own hosts: this page may load no image from another site. A picture the
 * platform has since moved simply goes, and the name stays.
 */
function AccountPicture({ channelId }: { channelId: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={here(`/v1/channels/${channelId}/avatar`)} alt="" width={18} height={18}
      onError={() => setFailed(true)} className="w-[18px] h-[18px] rounded-full object-cover border border-line"
    />
  );
}

